/**
 * 문서 서식 엔진 API 클라이언트.
 * 설계: docs/designs/core-document-template-engine.md §5.4
 */
import type {
  DocTemplateLayout,
  DocumentRenderContext,
  DocumentTypeCatalog,
} from '@axsolutionsdev/shared';

import { getApiClient } from './client';

export interface DocumentTemplateRow {
  id: string;
  docType: string;
  code: string;
  name: string;
  layout: DocTemplateLayout;
  version: number;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentRenderResponse {
  template: {
    id: string | null;
    code: string | null;
    name: string;
    version: number;
    /** 저장된 서식 없이 공급자 프리셋으로 렌더된 경우 */
    isPreset: boolean;
    layout: DocTemplateLayout;
  };
  /**
   * 렌더 컨텍스트. **`data` 로 이름 붙이면 안 된다** — `unwrapEnvelopeResponse` 가
   * `{ data, meta? }` 를 표준 envelope 으로 오인해 `template` 을 버린다.
   */
  context: DocumentRenderContext;
}

export interface ListDocumentTemplatesParams {
  docType?: string;
  keyword?: string;
  includeHistory?: boolean;
  includeInactive?: boolean;
}

export interface SaveDocumentTemplateBody {
  docType?: string;
  name?: string;
  layout?: DocTemplateLayout;
  code?: string;
  note?: string | null;
  sortOrder?: number;
  isDefault?: boolean;
  /** PATCH 전용 — 사용/중지 토글 */
  isActive?: boolean;
}

/** 인쇄 화면의 유일한 호출 — 서식 + 데이터 + 회사정보 + 발행 meta. */
export async function fetchDocumentRender(
  docType: string,
  sourceId: string,
  templateId?: string,
): Promise<DocumentRenderResponse> {
  const res = await getApiClient().get<DocumentRenderResponse>('/documents/render', {
    params: { docType, sourceId, ...(templateId ? { templateId } : {}) },
  });
  return res.data;
}

/** 이 앱에 등록된 문서 종류 — 디자이너 문서종류 셀렉트. */
export async function fetchDocumentTypes(): Promise<DocumentTypeCatalog[]> {
  const res = await getApiClient().get<DocumentTypeCatalog[]>('/documents/types');
  return res.data;
}

/** 바인딩 카탈로그 — 디자이너 [데이터 연결] 드롭다운. */
export async function fetchDocumentCatalog(docType: string): Promise<DocumentTypeCatalog> {
  const res = await getApiClient().get<DocumentTypeCatalog>(
    `/documents/types/${encodeURIComponent(docType)}/catalog`,
  );
  return res.data;
}

export interface DocumentSampleSource {
  id: string;
  label: string;
  subLabel?: string;
}

/** 디자이너 실데이터 미리보기용 최근 원천 레코드 (설계 §7.6.3). */
export async function fetchDocumentSamples(
  docType: string,
  limit = 20,
): Promise<DocumentSampleSource[]> {
  const res = await getApiClient().get<DocumentSampleSource[]>(
    `/documents/types/${encodeURIComponent(docType)}/samples`,
    { params: { limit } },
  );
  return res.data;
}

/** 공급자 기본 서식 — 「프리셋으로 시작」. */
export async function fetchDocumentPresetLayout(docType: string): Promise<DocTemplateLayout> {
  const res = await getApiClient().get<DocTemplateLayout>(
    `/documents/types/${encodeURIComponent(docType)}/preset`,
  );
  return res.data;
}

export async function listDocumentTemplates(
  params: ListDocumentTemplatesParams = {},
): Promise<DocumentTemplateRow[]> {
  const res = await getApiClient().get<DocumentTemplateRow[]>('/documents/templates', {
    params: {
      ...(params.docType ? { docType: params.docType } : {}),
      ...(params.keyword ? { keyword: params.keyword } : {}),
      ...(params.includeHistory ? { includeHistory: 'true' } : {}),
      ...(params.includeInactive ? { includeInactive: 'true' } : {}),
    },
  });
  return res.data;
}

export async function getDocumentTemplate(id: string): Promise<DocumentTemplateRow> {
  const res = await getApiClient().get<DocumentTemplateRow>(
    `/documents/templates/${encodeURIComponent(id)}`,
  );
  return res.data;
}

export async function createDocumentTemplate(
  body: SaveDocumentTemplateBody,
): Promise<DocumentTemplateRow> {
  const res = await getApiClient().post<DocumentTemplateRow>('/documents/templates', body);
  return res.data;
}

export async function updateDocumentTemplate(
  id: string,
  body: SaveDocumentTemplateBody,
): Promise<DocumentTemplateRow> {
  const res = await getApiClient().patch<DocumentTemplateRow>(
    `/documents/templates/${encodeURIComponent(id)}`,
    body,
  );
  return res.data;
}

/** 개정 — 구버전 보존 + version+1 신규 행 */
export async function reviseDocumentTemplate(
  id: string,
  body: Pick<SaveDocumentTemplateBody, 'layout' | 'name' | 'note'> = {},
): Promise<DocumentTemplateRow> {
  const res = await getApiClient().post<DocumentTemplateRow>(
    `/documents/templates/${encodeURIComponent(id)}/revise`,
    body,
  );
  return res.data;
}

export async function setDefaultDocumentTemplate(id: string): Promise<DocumentTemplateRow> {
  const res = await getApiClient().post<DocumentTemplateRow>(
    `/documents/templates/${encodeURIComponent(id)}/default`,
    {},
  );
  return res.data;
}

export async function deleteDocumentTemplate(id: string): Promise<void> {
  await getApiClient().delete(`/documents/templates/${encodeURIComponent(id)}`);
}
